import React from "react";
import { cva, type VariantProps } from "class-variance-authority";

const tacticLoaderVariants = cva(
  "flex items-center justify-center overflow-visible",
  {
    variants: {
      size: {
        xxs: "w-2 h-2",
        xs: "w-4 h-4",
        sm: "w-8 h-8",
        md: "w-12 h-12",
        lg: "w-16 h-16",
        xl: "w-24 h-24"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);

export interface TacticLoaderProps
  extends VariantProps<typeof tacticLoaderVariants> {
  /** Any CSS colour, or "currentColor" to follow the text. Default the brand blue. */
  color?: string;
  className?: string;
}

/** The Tactic logo dots lighting up in sequence, announced as "Loading". */

export const TacticLoader: React.FC<TacticLoaderProps> = ({
  size,
  color = "#49A8CE",
  className = ""
}) => {
  const getAnimationClass = (index: number) => {
    return `tactic-loader-animate-pulse-sequence-${index}`;
  };

  return (
    <div
      className={tacticLoaderVariants({ size, className })}
      role="status"
      aria-label="Loading"
    >
      <svg
        aria-hidden
        viewBox="0 0 53 53"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full overflow-visible"
        style={{ color, overflow: "visible" }}
      >
        <path
          d="M4.97923 27.9511C3.84016 29.0902 1.99337 29.0902 0.854301 27.9511C-0.284767 26.812 -0.284767 24.9652 0.854301 23.8262C1.99337 22.6871 3.84016 22.6871 4.97923 23.8262C6.1183 24.9652 6.1183 26.812 4.97923 27.9511Z"
          fill="currentColor"
          className={getAnimationClass(0)}
        />
        <path
          d="M17.5832 16.2638C16.3175 17.5294 14.2656 17.5294 12.9999 16.2638C11.7343 14.9982 11.7343 12.9462 12.9999 11.6806C14.2656 10.4149 16.3175 10.4149 17.5832 11.6806C18.8488 12.9462 18.8488 14.9982 17.5832 16.2638Z"
          fill="currentColor"
          className={getAnimationClass(1)}
        />
        <path
          d="M29.0413 6.63897C27.5226 8.15773 25.0602 8.15773 23.5414 6.63897C22.0227 5.12022 22.0227 2.65782 23.5414 1.13907C25.0602 -0.379689 27.5226 -0.379689 29.0413 1.13907C30.5601 2.65782 30.5601 5.12022 29.0413 6.63897Z"
          fill="currentColor"
          className={getAnimationClass(2)}
        />
        <path
          d="M51.0409 28.6386C49.5222 30.1574 47.0598 30.1574 45.541 28.6386C44.0223 27.1198 44.0223 24.6574 45.541 23.1387C47.0598 21.6199 49.5222 21.6199 51.0409 23.1387C52.5597 24.6574 52.5597 27.1198 51.0409 28.6386Z"
          fill="currentColor"
          className={getAnimationClass(3)}
        />
        <path
          d="M28.3538 51.3257C27.2148 52.4648 25.368 52.4648 24.2289 51.3257C23.0898 50.1866 23.0898 48.3398 24.2289 47.2008C25.368 46.0617 27.2148 46.0617 28.3538 47.2008C29.4929 48.3398 29.4929 50.1866 28.3538 51.3257Z"
          fill="currentColor"
          className={getAnimationClass(4)}
        />
        <path
          d="M16.6665 39.6384C15.5275 40.7775 13.6807 40.7775 12.5416 39.6384C11.4025 38.4993 11.4025 36.6525 12.5416 35.5135C13.6807 34.3744 15.5275 34.3744 16.6665 35.5135C17.8056 36.6525 17.8056 38.4993 16.6665 39.6384Z"
          fill="currentColor"
          className={getAnimationClass(5)}
        />
        <path
          d="M40.4994 39.1801C39.2338 40.4457 37.1818 40.4457 35.9162 39.1801C34.6506 37.9144 34.6506 35.8625 35.9162 34.5968C37.1818 33.3312 39.2338 33.3312 40.4994 34.5968C41.7651 35.8625 41.7651 37.9144 40.4994 39.1801Z"
          fill="currentColor"
          className={getAnimationClass(6)}
        />
        <path
          d="M40.0411 18.5554C38.2692 20.3273 35.3965 20.3273 33.6246 18.5554C31.8527 16.7836 31.8527 13.9108 33.6246 12.1389C35.3965 10.367 38.2692 10.367 40.0411 12.1389C41.813 13.9108 41.813 16.7836 40.0411 18.5554Z"
          fill="currentColor"
          className={getAnimationClass(7)}
        />
        <path
          d="M29.0413 28.6386C27.5226 30.1574 25.0602 30.1574 23.5414 28.6386C22.0227 27.1198 22.0227 24.6574 23.5414 23.1387C25.0602 21.6199 27.5226 21.6199 29.0413 23.1387C30.5601 24.6574 30.5601 27.1198 29.0413 28.6386Z"
          fill="currentColor"
          className={getAnimationClass(8)}
        />
      </svg>
    </div>
  );
};
